import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import Logo from "../components/Logo";
import { formatApiErrorDetail } from "../lib/api";
import { Lock, ShieldCheck } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate("/admin");
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 gap-10 items-center" data-testid="auth-page">
      <div className="hidden lg:block relative h-[520px] rounded-3xl overflow-hidden">
        <div className="absolute inset-0 bodh-gradient" />
        <div className="absolute inset-0 bg-black/30" />
        <div className="absolute inset-0 bodh-world-bg opacity-40" />
        <div className="absolute inset-0 p-10 text-white flex flex-col justify-between">
          <Logo size="lg" variant="light" />
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-md text-xs font-outfit font-bold uppercase tracking-widest mb-4">
              <ShieldCheck className="h-3.5 w-3.5" /> Staff Login Only
            </div>
            <h2 className="font-outfit font-black text-4xl leading-tight">Welcome to the Bodh Networks Newsroom.</h2>
            <p className="mt-3 text-white/90 font-outfit max-w-md">
              This login is for authorised reporters, editors and admins only. To request a new account, please contact your admin.
            </p>
            <ul className="mt-5 space-y-2 font-outfit text-sm text-white/95">
              <li>• <b>Admin:</b> Full access — users, settings, articles.</li>
              <li>• <b>Editor:</b> Edit, approve and publish articles.</li>
              <li>• <b>Reporter:</b> Write your articles and submit them for review.</li>
            </ul>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="bg-white rounded-3xl p-8 border border-zinc-100 shadow-sm" data-testid="auth-form">
        <div className="lg:hidden mb-6"><Logo size="md" /></div>
        <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-red-50 text-[#E30613] text-[10px] font-outfit font-black uppercase tracking-widest">
          <Lock className="h-3 w-3" /> Newsroom Staff
        </div>
        <h1 className="mt-3 font-outfit font-black text-3xl text-zinc-900">Sign in</h1>
        <p className="text-sm text-zinc-500 mt-1 font-outfit">Authorised staff only. Get your password from the admin.</p>

        <div className="mt-5">
          <label className="text-xs font-outfit font-bold uppercase tracking-widest text-zinc-500">Email</label>
          <input
            required
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="auth-email"
            placeholder="you@bodhnetworks.com"
            className="mt-1.5 w-full px-4 py-3 rounded-xl border border-zinc-200 focus:border-[#E30613] outline-none"
          />
        </div>
        <div className="mt-4">
          <label className="text-xs font-outfit font-bold uppercase tracking-widest text-zinc-500">Password</label>
          <input
            required
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            data-testid="auth-password"
            placeholder="Enter your password"
            className="mt-1.5 w-full px-4 py-3 rounded-xl border border-zinc-200 focus:border-[#E30613] outline-none"
          />
        </div>

        {err && <div className="mt-3 text-sm text-[#E30613] font-outfit" data-testid="auth-error">{err}</div>}

        <button
          disabled={loading}
          type="submit"
          data-testid="auth-submit"
          className="mt-6 w-full py-3 rounded-xl bodh-gradient text-white font-outfit font-bold disabled:opacity-60"
        >
          {loading ? "Signing in..." : "Sign in"}
        </button>

        <div className="mt-6 p-3 rounded-xl bg-amber-50 border border-amber-100 text-xs font-outfit text-amber-900">
          <b>Want to write for Bodh Networks?</b> Reach out to your admin to request an account.<br />
          <a href="mailto:admin@bodhnetworks.com" className="text-amber-800 underline">admin@bodhnetworks.com</a>
        </div>

        <div className="mt-4 text-xs text-zinc-400 font-outfit">
          <Link to="/" className="hover:text-[#E30613]">← Back to home</Link>
        </div>
      </form>
    </div>
  );
}
